import { FAQ } from './faq';
import { GLOSSARY } from './glossary';
import { HANDBOOK } from './handbook';
import { PARTNER } from './partner';
import type { HelpEntry } from './types';

export { FAQ, GLOSSARY, HANDBOOK, PARTNER };
export type * from './types';

/**
 * Every entry from all three tabs, flattened. Two jobs: the search box matches against
 * `text`, and a `see: [...]` reference resolves through {@link HELP_BY_ID} to the entry's
 * title and tab so a cross-link can be rendered (and so a rotted id can be caught by a test).
 */
export const HELP_ENTRIES: HelpEntry[] = [
  ...GLOSSARY.flatMap((g) =>
    g.terms.map(
      (t): HelpEntry => ({
        id: t.id,
        tab: 'glossary',
        title: t.term,
        text: [t.term, t.short, t.body].filter(Boolean).join(' '),
      }),
    ),
  ),
  ...HANDBOOK.flatMap((c) =>
    c.topics.map(
      (topic): HelpEntry => ({
        id: topic.id,
        tab: 'handbook',
        title: topic.title,
        text: [
          topic.title,
          topic.when,
          ...topic.steps.flatMap((s) => [s.do, s.note ?? '']),
          ...(topic.warnings ?? []),
        ]
          .filter(Boolean)
          .join(' '),
      }),
    ),
  ),
  ...FAQ.flatMap((g) =>
    g.items.map(
      (item): HelpEntry => ({
        id: item.id,
        tab: 'faq',
        title: item.q,
        text: [item.q, item.a].join(' '),
      }),
    ),
  ),
  ...PARTNER.flatMap((s) =>
    s.topics.map(
      (topic): HelpEntry => ({
        id: topic.id,
        tab: 'partner',
        title: topic.title,
        // Field names and their notes are searched too — an integrator looking up
        // `mediaUrls` or a "409" they just got should land on the topic that explains it.
        text: [
          topic.title,
          topic.intro ?? '',
          ...(topic.steps ?? []).flatMap((step) => [step.do, step.note ?? '']),
          ...(topic.fields ?? []).flatMap((f) => [f.name, f.label, f.note ?? '']),
          topic.code?.body ?? '',
          ...(topic.warnings ?? []),
        ]
          .filter(Boolean)
          .join(' '),
      }),
    ),
  ),
];

export const HELP_BY_ID: ReadonlyMap<string, HelpEntry> = new Map(
  HELP_ENTRIES.map((e) => [e.id, e]),
);

/**
 * Fold for search. Turkish casing is not the invariant one — `İSTANBUL`/`istanbul` only
 * match through the `tr` locale — so this mirrors the folding already used by the admin's
 * other client-side filters (`UsersPage`, `CompanyPicker`, `NewSalePage`).
 */
export function fold(s: string): string {
  return s.trim().toLocaleLowerCase('tr');
}

/** Ids of the entries matching `query`; an empty query matches everything. */
export function searchHelp(query: string): Set<string> {
  const needle = fold(query);
  if (!needle) return new Set(HELP_ENTRIES.map((e) => e.id));
  return new Set(HELP_ENTRIES.filter((e) => fold(e.text).includes(needle)).map((e) => e.id));
}
